import type { Paragraph, Parent, PhrasingContent, Root, RootContent } from 'mdast';
import type { Properties } from 'hast';

interface HProps {
  hName?: string;
  hProperties?: Properties;
}

type Line = PhrasingContent[];

/** Splits a paragraph's inline children on soft line breaks. */
const toLines = (children: PhrasingContent[]): Line[] => {
  const lines: Line[] = [[]];

  for (const child of children) {
    if (child.type === 'break') {
      lines.push([]);
      continue;
    }

    if (child.type === 'text' && child.value.includes('\n')) {
      const parts = child.value.split('\n');
      parts.forEach((part, index) => {
        if (index > 0) lines.push([]);
        if (part !== '') {
          lines[lines.length - 1].push({ ...child, value: part });
        }
      });
      continue;
    }

    lines[lines.length - 1].push(child);
  }

  return lines.filter((line) => line.length > 0);
};

const DEFINITION = /^:[ \t]+/;

const startsDefinition = (line: Line): boolean =>
  line[0]?.type === 'text' && DEFINITION.test(line[0].value);

const stripMarker = (line: Line): Line => {
  const [head, ...rest] = line;
  if (head.type !== 'text') return line;
  const value = head.value.replace(DEFINITION, '');
  return value === '' ? rest : [{ ...head, value }, ...rest];
};

const wrap = (hName: 'dt' | 'dd', children: PhrasingContent[]): Paragraph => ({
  type: 'paragraph',
  children,
  data: { hName } as HProps,
});

/**
 * Adds PHP-Markdown-Extra style definition lists:
 *
 * ```
 * Term
 * : Definition
 * ```
 *
 * remark parses this as a single paragraph, so the transform runs over
 * paragraph children and re-emits `dl`/`dt`/`dd` via `data.hName`.
 */
export default function remarkDefinitionLists() {
  return (tree: Root) => {
    const walk = (parent: Parent) => {
      for (let index = 0; index < parent.children.length; index += 1) {
        const child = parent.children[index] as RootContent;

        if ('children' in child && child.type !== 'paragraph') {
          walk(child as Parent);
          continue;
        }

        if (child.type !== 'paragraph') continue;

        const lines = toLines(child.children);
        if (lines.length < 2) continue;
        if (startsDefinition(lines[0])) continue;
        if (!lines.some(startsDefinition)) continue;

        const items: Paragraph[] = [];
        let sawTerm = false;

        for (const line of lines) {
          if (startsDefinition(line)) {
            if (!sawTerm) break;
            items.push(wrap('dd', stripMarker(line)));
          } else {
            sawTerm = true;
            items.push(wrap('dt', line));
          }
        }

        if (items.length === 0 || !items.some((item) => (item.data as HProps).hName === 'dd')) {
          continue;
        }

        parent.children[index] = {
          type: 'paragraph',
          children: items as unknown as PhrasingContent[],
          data: { hName: 'dl', hProperties: { className: ['definition-list'] } } as HProps,
        };
      }
    };

    walk(tree);
  };
}
